import React from 'react';
import { Inputs, PackageType, IndustryType, RevenueCadence, INDUSTRY_DEFAULTS } from '../utils/roiCalculations';
interface AssumptionsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  inputs: Inputs;
  onChange: (newInputs: Inputs) => void;
}
export function AssumptionsSidebar({
  isOpen,
  onClose,
  inputs,
  onChange
}: AssumptionsSidebarProps) {
  const handleChange = (field: keyof Inputs, value: string | number) => {
    onChange({
      ...inputs,
      [field]: value
    });
  };
  // Handle numeric input changes - allow empty string, convert to number only when valid
  const handleNumericChange = (field: keyof Inputs, value: string) => {
    // If empty, set to empty string to allow clearing
    if (value === '') {
      onChange({
        ...inputs,
        [field]: ''
      });
      return;
    }
    // Otherwise parse as float
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      onChange({
        ...inputs,
        [field]: numValue
      });
    }
  };
  const handleIndustryChange = (newIndustry: IndustryType) => {
    const defaults = INDUSTRY_DEFAULTS[newIndustry];
    onChange({
      ...inputs,
      industry: newIndustry,
      ticket: defaults.ticket,
      gpm: defaults.gpm,
      salesFirst90Total: defaults.salesFirst90Total,
      salesVetMonthly: defaults.salesVetMonthly,
      revenueCadence: defaults.revenueCadence,
      residualTicket: defaults.residualTicket
    });
  };
  return <>
      {/* Backdrop */}
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={onClose} data-id="element-1" data-mp="3hbh0epdse8ip" />}

      {/* Sidebar */}
      <div className={`fixed top-0 right-0 h-full w-full max-w-xs bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-2" data-mp="37mpb6p0422bh">
        <div className="h-full flex flex-col" data-id="element-3" data-mp="3xdnjwlzvqeg">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b-2 border-gray-200 bg-navy" data-id="element-4" data-mp="3zgld2te2ntq">
            <h2 className="text-lg font-bold text-white" data-id="element-5" data-mp="dr9ojsbdfrh7">
              Business Assumptions
            </h2>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" data-id="element-6" data-mp="4sxpd1wfvld5">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7" data-mp="3qzw023bdcwx5">
                <line x1="18" y1="6" x2="6" y2="18" data-id="element-8" data-mp="3d8zlhgea2g93" />
                <line x1="6" y1="6" x2="18" y2="18" data-id="element-9" data-mp="3dakf93srpv5w" />
              </svg>
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-4" data-id="element-10" data-mp="33atgur136w0j">
            <div className="space-y-4" data-id="element-11" data-mp="33bbn8ghgqqm6">
              {/* Company Info */}
              <div className="space-y-3" data-id="element-12" data-mp="2t5tlenlfj2lv">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-13" data-mp="2t8z9q0m8i0gj">
                  Company Info
                </h3>

                <div data-id="element-14" data-mp="334j76rgg577e">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-15" data-mp="3363xg4vqa3zg">
                    Company Name
                  </label>
                  <input type="text" value={inputs.companyName} onChange={e => handleChange('companyName', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-16" data-mp="2ag58e9hja6us" />
                </div>

                <div data-id="element-17" data-mp="2p6ukrf6qds08">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-18" data-mp="1rpvsxxwqkil5">
                    Industry
                  </label>
                  <select value={inputs.industry} onChange={e => handleIndustryChange(e.target.value as IndustryType)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-19" data-mp="1wnfivv2sqjsx">
                    <option value="" data-id="element-20" data-mp="c04r9dd1u7tj">Select</option>
                    <option data-id="element-21" data-mp="76jx5wektxzm">Windows</option>
                    <option data-id="element-22" data-mp="75s3ws8gccci">Roofing</option>
                    <option data-id="element-23" data-mp="ky883wd3agf0">Solar (Sales)</option>
                    <option data-id="element-24" data-mp="l12s5ms6xz7w">Solar (Vertical)</option>
                    <option data-id="element-25" data-mp="gwd0690nl4s9">Solar (Community)</option>
                    <option data-id="element-26" data-mp="gzr5nq4m5u3x">Pest</option>
                    <option data-id="element-27" data-mp="uw4kerhrjhep">Fiber</option>
                    <option data-id="element-28" data-mp="uv37ehh6skuc">Deregulated Energy</option>
                    <option data-id="element-29" data-mp="pxt726s9eorq">Hardscaping</option>
                    <option data-id="element-30" data-mp="pw0534slcsq2">Landscaping</option>
                    <option data-id="element-31" data-mp="2wnr5kcez4i39">Other</option>
                  </select>
                </div>

                <div data-id="element-32" data-mp="2rpzf7mtz9416">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-33" data-mp="2rqr82pw6wnnr">
                    Partnership
                  </label>
                  <select value={inputs.pkg} onChange={e => handleChange('pkg', e.target.value as PackageType)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none font-semibold" data-id="element-34" data-mp="179rjq7m5wv11">
                    <option value="" data-id="element-35" data-mp="ofvzxqy49cnj">Select</option>

                    <option value="advantage" data-id="element-36" data-mp="oja2p0aoznvi">HIRE Advantage</option>
                    <option value="summit" data-id="element-37" data-mp="thlcjb0zlydq">HIRE Summit</option>
                  </select>
                </div>
              </div>

              {/* Campaign & Ad Spend */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-38" data-mp="jivtvg19o9wf">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-39" data-mp="28x0oc0k7j3gf">
                  Campaign & Ad Spend
                </h3>

                <div data-id="element-40" data-mp="2dvbwm6uut5wj">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-41" data-mp="2dtrclb6615cx">
                    Number of Campaigns
                  </label>
                  <input type="number" min="0.5" step="0.5" value={inputs.campaigns} onChange={e => handleNumericChange('campaigns', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-42" data-mp="27fc0bucs5svv" />
                </div>

                <div data-id="element-43" data-mp="2q864rucs22lu">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-44" data-mp="2lf4opj0u3x0o">
                    Ad Spend per Month (per campaign)
                  </label>
                  <div className="relative" data-id="element-45" data-mp="2hdj7xml6iyl7">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-46" data-mp="3dw7c4zy7zczo">
                      $
                    </span>
                    <input type="number" min="0" step="100" value={inputs.adSpend} onChange={e => handleNumericChange('adSpend', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-47" data-mp="393nu9qsi1vp2" />
                  </div>
                </div>

                <div data-id="element-48" data-mp="1ekx4zzt6bgee">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-49" data-mp="1ejcjezou77so">
                    Ad Cost per Hire
                  </label>
                  <div className="relative" data-id="element-50" data-mp="1oc1jyziun198">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-51" data-mp="1odcmyx4h04za">
                      $
                    </span>
                    <input type="number" min="1" step="10" value={inputs.cph} onChange={e => handleNumericChange('cph', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-52" data-mp="1xcpxlt4byzll" />
                  </div>
                </div>
              </div>

              {/* Revenue & Margins */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-53" data-mp="1i4uodo0f9dag">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-54" data-mp="uhmhx2r9by6u">
                  Revenue & Margins
                </h3>

                <div data-id="element-55" data-mp="139esrek719by">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-56" data-mp="139oh6xplb8fj">
                    Revenue Cadence
                  </label>
                  <select value={inputs.revenueCadence} onChange={e => handleChange('revenueCadence', e.target.value as RevenueCadence)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-57" data-mp="ygllok195owp">
                    <option value="one-time" data-id="element-58" data-mp="3iv1y8btdefwr">One-Time Sale</option>
                    <option value="monthly" data-id="element-59" data-mp="3isp7b95mwv7k">Monthly Residual</option>
                    <option value="quarterly" data-id="element-60" data-mp="3nuoc8nh0w06m">Quarterly Residual</option>
                  </select>
                </div>

                <div data-id="element-61" data-mp="feuuk5ayginz">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-62" data-mp="fc8qwgr6qnb1">
                    {inputs.revenueCadence === 'one-time' ? 'Average Ticket' : 'Initial Sale Ticket'}
                  </label>
                  <div className="relative" data-id="element-63" data-mp="adozi0ttojba">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-64" data-mp="2q64h4c0wap5l">
                      $
                    </span>
                    <input type="number" min="0" step="500" value={inputs.ticket} onChange={e => handleNumericChange('ticket', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-65" data-mp="2v60kc91rqck3" />
                  </div>
                </div>

                {inputs.revenueCadence !== 'one-time' && <div data-id="element-66" data-mp="2sb0dt2tvpwxl">
                    <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-67" data-mp="2sba6wza80k7i">
                      {inputs.revenueCadence === 'monthly' ? 'Monthly Residual' : 'Quarterly Residual'}
                    </label>
                    <div className="relative" data-id="element-68" data-mp="bmd41n9rxhes">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-69" data-mp="bno7tn37yp5w">
                        $
                      </span>
                      <input type="number" min="0" step="10" value={inputs.residualTicket || 0} onChange={e => handleNumericChange('residualTicket', e.target.value)} className="w-full p-2 pl-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-70" data-mp="2138ozjq65z71" />
                    </div>
                  </div>}

                {inputs.revenueCadence === 'monthly' && <div data-id="element-71" data-mp="2tspby1v0snvv">
                    <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-72" data-mp="2tqur0njr0vrs">
                      Monthly Attrition
                    </label>
                    <div className="relative" data-id="element-73" data-mp="ze4z2bcxxwn1">
                      <input type="number" min="0" max="100" step="1" value={inputs.monthlyAttrition || 2} onChange={e => handleNumericChange('monthlyAttrition', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-74" data-mp="zexwumtx9bt1" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-75" data-mp="3dfnm4psscd5q">
                        %
                      </span>
                    </div>
                  </div>}

                {inputs.revenueCadence === 'quarterly' && <div data-id="element-76" data-mp="3r6abhrcnoqp5">
                    <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-77" data-mp="3r6sj1k4rqrcd">
                      Quarterly Attrition
                    </label>
                    <div className="relative" data-id="element-78" data-mp="4zkhyu0vt6m5">
                      <input type="number" min="0" max="100" step="1" value={inputs.quarterlyAttrition || 5} onChange={e => handleNumericChange('quarterlyAttrition', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-79" data-mp="4y9e6u7fryv1" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-80" data-mp="sy1mtpfigwd4">
                        %
                      </span>
                    </div>
                  </div>}

                <div data-id="element-81" data-mp="1gltafp6i4vtp">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-82" data-mp="1gm2znajq2yyc">
                    Gross Profit Margin
                  </label>
                  <div className="relative" data-id="element-83" data-mp="wcjlcko5wods">
                    <input type="number" min="0" max="100" step="1" value={inputs.gpm} onChange={e => handleNumericChange('gpm', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-84" data-mp="wbidsq6cuk0t" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-85" data-mp="1k2npct59iq5b">
                      %
                    </span>
                  </div>
                </div>
              </div>

              {/* Sales Performance */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-86" data-mp="1aahw7g39y2oj">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-87" data-mp="1y13vahvawcme">
                  Sales Performance
                </h3>

                <div data-id="element-88" data-mp="22uglgwfmorh9">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-89" data-mp="3niixdstiverm">
                    Sales per 90 Day Rep
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesFirst90Total} onChange={e => handleNumericChange('salesFirst90Total', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-90" data-mp="3ijo6e3leijwf" />
                </div>

                <div data-id="element-91" data-mp="3eje00ptp5rh1">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-92" data-mp="3ejxhk5ffqduj">
                    Monthly Sales per Veteran
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesVetMonthly} onChange={e => handleNumericChange('salesVetMonthly', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-93" data-mp="trlxw0j86qnb" />
                </div>

                <div data-id="element-94" data-mp="15sbszkrfpbli">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-95" data-mp="1athry0botv3h">
                    Pullthrough / Realization
                  </label>
                  <div className="relative" data-id="element-96" data-mp="d7alslzqjup0">
                    <input type="number" min="0" max="100" step="1" value={inputs.pullthrough} onChange={e => handleNumericChange('pullthrough', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-97" data-mp="i4kimmpgf8mv" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-98" data-mp="2com33p45tnd1">
                      %
                    </span>
                  </div>
                </div>
              </div>

              {/* Retention Model */}
              <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-99" data-mp="23mmiynxum35v">
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide" data-id="element-100" data-mp="1ypme2gcqj6gc">
                  Retention Model
                </h3>

                <div data-id="element-101" data-mp="1tr1codlqygqb">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-102" data-mp="1trkyw6ifjra5">
                    Hired to Launched
                  </label>
                  <div className="relative" data-id="element-103" data-mp="1kyhlfy7sbefi">
                    <input type="number" min="0" max="100" step="1" value={inputs.pctLaunched} onChange={e => handleNumericChange('pctLaunched', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-104" data-mp="1kv23mnmq7rae" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-105" data-mp="2ytu8ek5i5mbu">
                      %
                    </span>
                  </div>
                </div>

                <div data-id="element-106" data-mp="2pxcvbbdkvedg">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-107" data-mp="2b6ds6fvihi19">
                    Launched to Day 30
                  </label>
                  <div className="relative" data-id="element-108" data-mp="2fxvqsxa2b6j2">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct30} onChange={e => handleNumericChange('pct30', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-109" data-mp="225nypitq43qp" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-110" data-mp="1dl2noo2mgtlo">
                      %
                    </span>
                  </div>
                </div>

                <div data-id="element-111" data-mp="1nka0991j8k7q">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-112" data-mp="1ijvsls3ufca8">
                    Day 30 to Day 90
                  </label>
                  <div className="relative" data-id="element-113" data-mp="1wicbsiiw6v99">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct90} onChange={e => handleNumericChange('pct90', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-114" data-mp="1whaq7k492qda" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-115" data-mp="2bae6zpa0p02y">
                      %
                    </span>
                  </div>
                </div>

                <div data-id="element-116" data-mp="2iryefwh48jbi">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-117" data-mp="2iuki3l0vyeog">
                    Day 90 to 1 Year
                  </label>
                  <div className="relative" data-id="element-118" data-mp="2srfiyakmsr5w">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct1y} onChange={e => handleNumericChange('pct1y', e.target.value)} className="w-full p-2 pr-6 rounded border border-gray-300 text-sm focus:border-amber outline-none" data-id="element-119" data-mp="2sojk8k0ylyge" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-120" data-mp="1lb9c81o97f1l">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
}